// src/components/PhotoGalleryModal.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import '../scss/components/_photogallerymodal.scss'

const PhotoGalleryModal = ({ photos, onClose, initialTab = "All" }) => {
    console.log("photos", photos);
    const [activeTab, setActiveTab] = useState(initialTab);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [zoomLevel, setZoomLevel] = useState(1);
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [imageLoaded, setImageLoaded] = useState(false);
    const overlayRef = useRef(null);
    const imageContainerRef = useRef(null);
    const thumbnailContainerRef = useRef(null);

    // Group photos by PhotoType
    const getGroupedPhotos = () => {
        const groups = {};
        photos.forEach(photo => {
            const type = photo.PhotoType || "Other";
            if (!groups[type]) {
                groups[type] = [];
            }
            groups[type].push(photo);
        });
        return groups;
    };

    const groupedPhotos = getGroupedPhotos();

     const TAB_ORDER = ["PG", "SINGLE", "DOUBLE", "TRIPLE", "QUAD", "DORM"];

     const getPhotoTypesInOrder = () => {
        const types = ["All"];
        // First add types from TAB_ORDER that exist in groupedPhotos
        TAB_ORDER.forEach(type => {
            if (groupedPhotos[type]) {
                types.push(type);
            }
        });
        // Then add any remaining types not in TAB_ORDER
        Object.keys(groupedPhotos).forEach(type => {
            if (!types.includes(type)) {
                types.push(type);
            }
        });
        return types;
    };

    // const photoTypes = ["All", ...Object.keys(groupedPhotos)];

    const photoTypes = getPhotoTypesInOrder();

    // Get filtered photos based on active tab
    const getFilteredPhotos = () => {
        if (activeTab === "All") {
            return photos;
        }
        return groupedPhotos[activeTab] || [];
    };

    const filteredPhotos = getFilteredPhotos();

    // Get count for each type
    const getTypeCount = (type) => {
        if (type === "All") return photos.length;
        return groupedPhotos[type]?.length || 0;
    };

      // Format PhotoType for display
    const formatTypeName = (type) => {
        if (type === "PG") return "Common";
        if (type === "SINGLE") return "Single Sharing";
        if (type === "DOUBLE") return "Double Sharing";
        if (type === "TRIPLE") return "Triple Sharing";
        if (type === "QUAD") return "Quad Sharing";
        if (type === "DORM") return "Dorm Sharing";
        return type;
    };

    // Reset selected index when tab changes
    useEffect(() => {
        setSelectedIndex(0);
        setZoomLevel(1);
        setPosition({ x: 0, y: 0 });
        setImageLoaded(false);
    }, [activeTab]);

    // Handle keyboard shortcuts
    useEffect(() => {
        const handleKeydown = (e) => {
            if (e.key === "Escape") {
                onClose();
            } else if (e.key === "ArrowRight" || e.key === "Right") {
                handleNext();
            } else if (e.key === "ArrowLeft" || e.key === "Left") {
                handlePrev();
            } else if (e.key === "+" || e.key === "=") {
                handleZoomIn();
            } else if (e.key === "-") {
                handleZoomOut();
            }
        };
        window.addEventListener("keydown", handleKeydown);
        return () => window.removeEventListener("keydown", handleKeydown);
    }, [filteredPhotos, selectedIndex]);

    // Handle overlay click
    const handleOverlayClick = (e) => {
        if (e.target === overlayRef.current) onClose();
    };

    // Navigation handlers
    const handlePrev = useCallback(() => {
        if (filteredPhotos.length === 0) return;
        setSelectedIndex((prev) => (prev - 1 + filteredPhotos.length) % filteredPhotos.length);
        setZoomLevel(1);
        setPosition({ x: 0, y: 0 });
        setImageLoaded(false);
    }, [filteredPhotos.length]);

    const handleNext = useCallback(() => {
        if (filteredPhotos.length === 0) return;
        setSelectedIndex((prev) => (prev + 1) % filteredPhotos.length);
        setZoomLevel(1);
        setPosition({ x: 0, y: 0 });
        setImageLoaded(false);
    }, [filteredPhotos.length]);

    // Zoom handlers
    const handleZoomIn = () => {
        setZoomLevel((prev) => Math.min(prev + 0.25, 3));
    };

    const handleZoomOut = () => {
        setZoomLevel((prev) => Math.max(prev - 0.25, 0.5));
        if (zoomLevel <= 0.5) {
            setPosition({ x: 0, y: 0 });
        }
    };

    const handleZoomReset = () => {
        setZoomLevel(1);
        setPosition({ x: 0, y: 0 });
    };

    // Drag handlers for zoomed images
    const handleMouseDown = (e) => {
        if (zoomLevel <= 1) return;
        setIsDragging(true);
        setDragStart({
            x: e.clientX - position.x,
            y: e.clientY - position.y
        });
    };

    const handleMouseMove = (e) => {
        if (!isDragging || zoomLevel <= 1) return;
        setPosition({
            x: e.clientX - dragStart.x,
            y: e.clientY - dragStart.y
        });
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    // Touch handlers for mobile
    const handleTouchStart = (e) => {
        if (zoomLevel <= 1) return;
        const touch = e.touches[0];
        setIsDragging(true);
        setDragStart({
            x: touch.clientX - position.x,
            y: touch.clientY - position.y
        });
    };

    const handleTouchMove = (e) => {
        if (!isDragging || zoomLevel <= 1) return;
        const touch = e.touches[0];
        setPosition({
            x: touch.clientX - dragStart.x,
            y: touch.clientY - dragStart.y
        });
    };

    const handleTouchEnd = () => {
        setIsDragging(false);
    };

    // Scroll thumbnail into view
    useEffect(() => {
        if (thumbnailContainerRef.current && selectedIndex >= 0) {
            const thumbnails = thumbnailContainerRef.current.querySelectorAll('.photo-thumb-item');
            if (thumbnails[selectedIndex]) {
                thumbnails[selectedIndex].scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                    inline: 'center'
                });
            }
        }
    }, [selectedIndex]);

    // // Format PhotoType for display
    // const formatTypeName = (type) => {
    //     if (type === "PG") return "Common";
    //     if (type === "SINGLE") return "Single Sharing";
    //     if (type === "DOUBLE") return "Double Sharing";
    //     if (type === "TRIPLE") return "Triple Sharing";
    //     if (type === "QUAD") return "Quad Sharing";
    //     if (type === "DORM") return "Dorm Sharing";
    //     return type;
    // };

    // Get current photo
    const currentPhoto = filteredPhotos[selectedIndex] || null;

    return (
        <div className="photo-gallery-overlay" ref={overlayRef} onClick={handleOverlayClick}>
            <div className="photo-gallery-modal">
                {/* Header */}
                <div className="photo-gallery-header">
                    {/* <h2 className="photo-gallery-title">
                    {activeTab === "All" 
                    ? `All Photos (${filteredPhotos.length})`
                    : `${formatTypeName(activeTab)} (${filteredPhotos.length})`
                    }
                </h2> */}

                    <div className="photo-gallery-tabs">
                        {photoTypes.map((type) => (
                            <button
                                key={type}
                                className={`photo-tab ${activeTab === type ? "active" : ""}`}
                                onClick={() => setActiveTab(type)}
                            >
                                {type === "All" ? "All Photos" : formatTypeName(type)}
                                <span className="photo-tab-count">{getTypeCount(type)}</span>
                            </button>
                        ))}
                    </div>
                    <button className="photo-gallery-close" onClick={onClose}>
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M2 2L18 18M18 2L2 18" stroke="#374151" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                    </button>
                </div>



                {/* Tabs */}
                {/* <div className="photo-gallery-tabs">
          {photoTypes.map((type) => (
            <button
              key={type}
              className={`photo-tab ${activeTab === type ? "active" : ""}`}
              onClick={() => setActiveTab(type)}
            >
              {type === "All" ? "All Photos" : formatTypeName(type)}
              <span className="photo-tab-count">{getTypeCount(type)}</span>
            </button>
          ))}
        </div> */}

                {/* Thumbnails */}
                <div className="photo-thumbnails-container" ref={thumbnailContainerRef}>
                    <div className="photo-thumbnails">
                        {filteredPhotos.length > 0 ? (
                            filteredPhotos.map((photo, index) => (
                                <div
                                    key={photo.PhotoID || index}
                                    className={`photo-thumb-item ${selectedIndex === index ? "active" : ""}`}
                                    onClick={() => {
                                        setSelectedIndex(index);
                                        setZoomLevel(1);
                                        setPosition({ x: 0, y: 0 });
                                        setImageLoaded(false);
                                    }}
                                >
                                    <img
                                        src={photo.PhotoURL}
                                        alt={photo.PhotoTitle || `Photo ${index + 1}`}
                                        loading="lazy"
                                    />
                                    {photo.PhotoType && photo.PhotoType !== "PG" && (
                                        <span className="photo-thumb-badge">
                                            {photo.PhotoType === "SINGLE" && "S"}
                                            {photo.PhotoType === "DOUBLE" && "D"}
                                            {photo.PhotoType === "TRIPLE" && "T"}
                                            {photo.PhotoType === "QUAD" && "Q"}
                                            {photo.PhotoType === "DORM" && "D"}
                                        </span>
                                    )}
                                </div>
                            ))
                        ) : (
                            <div className="photo-thumbs-empty">
                                <p>No photos in this category</p>
                            </div>
                        )}
                    </div>
                </div>




                {/* Main Image Area */}
                <div className="photo-main-area">
                    {/* Left Arrow */}
                    <button
                        className="photo-nav-btn photo-nav-prev"
                        onClick={handlePrev}
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>

                    {/* Main Image Container */}
                    <div
                        className="photo-main-container"
                        ref={imageContainerRef}
                        onMouseDown={handleMouseDown}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUp}
                        onMouseLeave={handleMouseUp}
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                    >
                        {currentPhoto ? (
                            <>
                                {/* Image Info - Top Left */}
                                <div className="photo-image-info">
                                    {/* <span className="photo-image-title">
                                        {currentPhoto.PhotoTitle || `Photo ${selectedIndex + 1}`}
                                    </span> */}
                                    {currentPhoto.PhotoType && (
                                        <span className="photo-image-type">
                                            {formatTypeName(currentPhoto.PhotoType)}
                                        </span>
                                    )}
                                </div>
                                <div
                                    className="photo-main-wrapper"
                                    style={{
                                        transform: `translate(${position.x}px, ${position.y}px) scale(${zoomLevel})`,
                                        cursor: zoomLevel > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
                                        transition: isDragging ? 'none' : 'transform 0.2s ease'
                                    }}
                                >
                                    <img
                                        src={currentPhoto.PhotoURL}
                                        alt={currentPhoto.PhotoTitle || "Photo"}
                                        className="photo-main-image"
                                        onLoad={() => setImageLoaded(true)}
                                    />
                                </div>
                            </>
                        ) : (
                            <div className="photo-main-empty">
                                <p>No photos available</p>
                            </div>
                        )}

                        {/* Image Counter */}
                        {filteredPhotos.length > 0 && (
                            <div className="photo-counter">
                                {selectedIndex + 1} / {filteredPhotos.length}
                            </div>
                        )}
                    </div>

                    {/* Right Arrow */}
                    <button
                        className="photo-nav-btn photo-nav-next"
                        onClick={handleNext}
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M9 18L15 12L9 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>
                </div>

                {/* Controls */}
                <div className="photo-controls">
                    <button className="photo-control-btn" onClick={handleZoomOut} title="Zoom Out">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="5" y1="12" x2="19" y2="12" />
                        </svg>
                    </button>
                    <button className="photo-control-btn" onClick={handleZoomReset} title="Reset Zoom">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="11" cy="11" r="8" />
                            <line x1="21" y1="21" x2="16.65" y2="16.65" />
                        </svg>
                    </button>
                    <button className="photo-control-btn" onClick={handleZoomIn} title="Zoom In">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="11" cy="11" r="8" />
                            <line x1="21" y1="21" x2="16.65" y2="16.65" />
                            <line x1="11" y1="8" x2="11" y2="14" />
                            <line x1="8" y1="11" x2="14" y2="11" />
                        </svg>
                    </button>

                </div>
            </div>
        </div>
    );
};

export default PhotoGalleryModal;

